import { useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { useUser, useWorkspace } from 'app/utils'
import type { TabType } from 'app/components/ui/nav-tabs'
import { compact } from 'lodash'
import { useUserStore, useAuthStore } from 'app/stores/StoreContext'

const enum Sections {
  WORKSPACE = `workspace`,
  PROFILE = `profile`,
  NOTIFICATIONS = `notifications`,
}

export type UseUserViewReturn = {
  pageTitle: string
  tabs: TabType[]
  showTabs: boolean
  userId: string
}

export function useUserView(): UseUserViewReturn {
  const me = useUser()

  const { products } = useAuthStore()
  const { userId } = useParams()
  const isOrg = useIsOrgUser()
  const isSuperUser = me?.type === `super`
  const notificationsToggleEnabled = useFeatureToggle(`notifications`)
  const aiAgentOnlyNotifications = useFeatureToggle(`ai-agent-only-notifications`)
  const callProcessingProductEnabled = products?.includes(`Call Processing`)
  const schedulingProductEnabled = products?.includes(`Scheduler`)
  const isAiAgentOnly = products?.includes(`AI Agent`) && !products?.includes(`CRM`)
  const showNotifications =
    notificationsToggleEnabled &&
    (callProcessingProductEnabled || schedulingProductEnabled) &&
    (!isAiAgentOnly || aiAgentOnlyNotifications)

  const userStore = useUserStore()

  useEffect(() => {
    if (!userId || userId === `new` || userId === me?.id) return
    userStore.fetchOne(userId)
  }, [userId])

  const user = userStore.current as { id: string; name?: string } | undefined

  const isViewingAnotherUser = !!userId && userId !== `new` && userId !== me?.id
  const showTabs = (isSuperUser || isOrg) && isViewingAnotherUser

  let pageTitle: string
  if (userId === `new`) {
    pageTitle = `Add New User`
  } else if (userId === me?.id) {
    pageTitle = location.pathname.includes(`notifications`) ? `Notifications` : `Profile`
  } else {
    pageTitle = `Edit User: ${user?.name || ``}`
  }

  const tabs: TabType[] = compact([
    { name: `Workspace`, href: Sections.WORKSPACE },
    (isSuperUser || isOrg) && { name: `Profile`, href: Sections.PROFILE },
    (isSuperUser || isOrg) && showNotifications && { name: `Notifications`, href: Sections.NOTIFICATIONS },
  ])

  return {
    pageTitle,
    tabs,
    showTabs,
    userId: userId!,
  }
}
